import React, { useMemo, useState } from "react";
import {
  useListCommissionPlans,
  useUpdateCommissionPlanRates,
  getListCommissionPlansQueryKey,
  type CommissionPlan,
  type CommissionRateInput,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { formatZAR } from "@/lib/formatters";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  Pencil,
  Plus,
  Save,
  X,
  Users,
  Award,
  Layers,
} from "lucide-react";

function randToCents(rand: string): number {
  const n = Number.parseFloat(rand);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function centsToRand(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function Commission() {
  const plansQuery = useListCommissionPlans();
  const plans = plansQuery.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0B132B]">Commission</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configure per-package once-off rates, the monthly free-sale threshold
          and the retainer for each plan. Assign a plan to an individual agent
          from their profile page.
        </p>
      </div>

      {plansQuery.isLoading ? (
        <div className="py-16 text-center text-gray-400 text-sm">
          <Loader2 size={18} className="inline animate-spin mr-2" />
          Loading plans…
        </div>
      ) : plansQuery.error ? (
        <div className="py-16 text-center text-red-500 text-sm">
          Failed to load commission plans.
        </div>
      ) : (
        <div className="space-y-6">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      )}
    </div>
  );
}

function PlanCard({ plan }: { plan: CommissionPlan }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const updateRates = useUpdateCommissionPlanRates();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CommissionRateInput[]>([]);

  function startEdit() {
    setDraft(
      plan.rates
        .slice()
        .sort((a, b) => a.packageKey.localeCompare(b.packageKey))
        .map((r) => ({
          packageKey: r.packageKey,
          label: r.label,
          commissionCents: r.commissionCents,
        })),
    );
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setDraft([]);
  }

  function setRand(index: number, rand: string) {
    setDraft((d) =>
      d.map((r, i) =>
        i === index ? { ...r, commissionCents: randToCents(rand) } : r,
      ),
    );
  }

  function addRow() {
    setDraft((d) => [...d, { packageKey: "", label: "", commissionCents: 0 }]);
  }

  function setKey(index: number, key: string) {
    setDraft((d) => d.map((r, i) => (i === index ? { ...r, packageKey: key } : r)));
  }

  function setLabel(index: number, label: string) {
    setDraft((d) => d.map((r, i) => (i === index ? { ...r, label } : r)));
  }

  async function save() {
    const rates = draft
      .map((r) => ({
        packageKey: r.packageKey.trim(),
        label: r.label.trim() || r.packageKey.trim(),
        commissionCents: r.commissionCents,
      }))
      .filter((r) => r.packageKey.length > 0);
    try {
      await updateRates.mutateAsync({ id: plan.id, data: { rates } });
      queryClient.invalidateQueries({ queryKey: getListCommissionPlansQueryKey() });
      toast({ title: "Rates saved", description: `${plan.name} updated.` });
      setEditing(false);
      setDraft([]);
    } catch {
      toast({
        title: "Could not save rates",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  }

  const sortedRates = useMemo(
    () =>
      plan.rates
        .slice()
        .sort((a, b) => a.packageKey.localeCompare(b.packageKey)),
    [plan.rates],
  );

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 border-b border-gray-100">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-bold text-base text-[#0B132B]">{plan.name}</h2>
            {plan.isDefault && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-[#E6007E]/10 text-[#E6007E]">
                Default
              </span>
            )}
          </div>
          {plan.description && (
            <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
          )}
        </div>
        {editing ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={cancel}
              disabled={updateRates.isPending}
              className="rounded-md"
            >
              <X className="w-4 h-4 mr-1.5" /> Cancel
            </Button>
            <Button
              onClick={save}
              disabled={updateRates.isPending}
              className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-md"
            >
              {updateRates.isPending ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1.5" />
              )}
              Save
            </Button>
          </div>
        ) : (
          <Button variant="outline" onClick={startEdit} className="rounded-md">
            <Pencil className="w-4 h-4 mr-1.5" /> Edit rates
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 border-b border-gray-100">
        <Stat
          icon={Users}
          label="Agents on plan"
          value={(plan.agentCount ?? 0).toLocaleString("en-ZA")}
        />
        <Stat
          icon={Layers}
          label="Free sales / month"
          value={String(plan.thresholdFreeCount)}
        />
        <Stat
          icon={Award}
          label="Retainer"
          value={formatZAR(plan.retainerCents)}
        />
        <Stat
          icon={Award}
          label="Retainer KPI"
          value={`${plan.retainerMinLeads} leads · ${plan.retainerMinActivations} act.`}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
            <tr>
              <th className="px-5 py-3">Package key</th>
              <th className="px-5 py-3">Label</th>
              <th className="px-5 py-3 text-right">Once-off (R)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {!editing &&
              sortedRates.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50/60">
                  <td className="px-5 py-3 font-mono text-xs">{r.packageKey}</td>
                  <td className="px-5 py-3 text-gray-700">{r.label}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-medium">
                    {formatZAR(r.commissionCents)}
                  </td>
                </tr>
              ))}
            {!editing && sortedRates.length === 0 && (
              <tr>
                <td colSpan={3} className="px-5 py-8 text-center text-gray-400 text-sm">
                  No rates configured.
                </td>
              </tr>
            )}
            {editing &&
              draft.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50/60">
                  <td className="px-5 py-2">
                    <Input
                      value={r.packageKey}
                      onChange={(e) => setKey(i, e.target.value)}
                      placeholder="web_connect_20"
                      className="h-9 font-mono text-xs"
                    />
                  </td>
                  <td className="px-5 py-2">
                    <Input
                      value={r.label}
                      onChange={(e) => setLabel(i, e.target.value)}
                      placeholder="Web Connect 20"
                      className="h-9"
                    />
                  </td>
                  <td className="px-5 py-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={centsToRand(r.commissionCents)}
                      onChange={(e) => setRand(i, e.target.value)}
                      className="h-9 text-right tabular-nums"
                    />
                  </td>
                </tr>
              ))}
            {editing && (
              <tr>
                <td colSpan={3} className="px-5 py-3">
                  <button
                    type="button"
                    onClick={addRow}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#E6007E] hover:underline"
                  >
                    <Plus className="w-4 h-4" /> Add package rate
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-semibold text-[#0B132B] mt-0.5">{value}</p>
      </div>
    </div>
  );
}
